import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, dateFmt, formatApiError } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import {
    MapPin, Phone, Activity, Smartphone, CheckCircle2, Navigation,
    Clock, FileText, RefreshCw, ArrowRight, AlertTriangle, Hospital, Radio,
    Calculator, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import DownloadManualButton from "@/components/DownloadManualButton";
import ShiftCard from "@/components/ShiftCard";
import QuickQuoteModal from "@/components/QuickQuoteModal";
import ServiceChargesSheet from "@/components/ServiceChargesSheet";

/**
 * Flujo lineal del paramédico/operador a nivel UI.
 * Cada estado actual mapea al siguiente paso recomendado.
 * Permite saltos manuales desde el botón "Otro estatus" si hace falta.
 */
const NEXT_STEP = {
    unidad_asignada: { next: "en_salida", label: "Aceptar y salir", color: "bg-red-600 hover:bg-red-700" },
    tripulacion_notificada: { next: "en_salida", label: "Aceptar y salir", color: "bg-red-600 hover:bg-red-700" },
    en_salida: { next: "en_ruta_origen", label: "En ruta a origen", color: "bg-slate-900 hover:bg-slate-800" },
    en_ruta_origen: { next: "arribo_origen", label: "Llegué al origen", color: "bg-amber-600 hover:bg-amber-700" },
    arribo_origen: { next: "valoracion_inicial", label: "Iniciar valoración", color: "bg-amber-600 hover:bg-amber-700" },
    valoracion_inicial: { next: "paciente_abordo", label: "Paciente abordo", color: "bg-emerald-600 hover:bg-emerald-700" },
    paciente_abordo: { next: "en_ruta_destino", label: "En ruta a destino", color: "bg-slate-900 hover:bg-slate-800" },
    en_ruta_destino: { next: "arribo_destino", label: "Llegué al destino", color: "bg-amber-600 hover:bg-amber-700" },
    arribo_destino: { next: "entrega_paciente", label: "Paciente entregado", color: "bg-emerald-600 hover:bg-emerald-700" },
    entrega_paciente: { next: "servicio_finalizado", label: "Finalizar servicio", color: "bg-emerald-700 hover:bg-emerald-800" },
};

const FINISHED_STATES = new Set(["servicio_finalizado", "reporte_elaboracion", "reporte_cerrado", "facturado", "cobrado", "cancelado", "rechazado"]);

const TABS = [
    { id: "activos", label: "Activos", icon: Activity },
    { id: "pendientes", label: "Por recibir", icon: AlertTriangle },
    { id: "historico", label: "Histórico", icon: Clock },
];

/**
 * Captura una sola posición GPS (timeout 6s).
 * Resuelve a null si no hay permiso o GPS — la actualización no debe bloquearse.
 */
function getPosition() {
    return new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 },
        );
    });
}

function gmapsUrl(addr, lat, lng) {
    if (lat && lng) return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    if (addr) return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
    return null;
}

export default function CrewMobile() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [tab, setTab] = useState("activos");
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [tracking, setTracking] = useState(() => {
        // Persiste preferencia local — si el usuario activó el tracking,
        // sigue activo aunque cierre y vuelva a abrir la app.
        try { return localStorage.getItem("findmed_gps_tracking") === "1"; } catch { return false; }
    });
    const [lastPing, setLastPing] = useState(null);
    const [quickQuoteOpen, setQuickQuoteOpen] = useState(false);
    const [chargesService, setChargesService] = useState(null);
    const [unitInfo, setUnitInfo] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/services?limit=200");
            if (Array.isArray(data)) setServices(data);
        } catch (e) {
            console.warn(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, [load]);

    // Cargar info de la unidad cuando el login es de ambulancia
    useEffect(() => {
        if (user?.role === "ambulancia" && user?.unit_id) {
            (async () => {
                try {
                    const r = await api.get(`/units/${user.unit_id}`);
                    if (r?.data?.unit) setUnitInfo(r.data.unit);
                } catch (e) { void e; }
            })();
        }
    }, [user]);

    // Categorizar servicios por pestaña.
    // El usuario puede ser un paramédico individual (rol paramedico/operador/medico)
    // o un login de UNIDAD (rol "ambulancia" con unit_id asignado).
    const { activos, pendientes, historico, mine } = useMemo(() => {
        const isMine = (s) => {
            if (user?.role === "ambulancia" && user?.unit_id) {
                return s.unit_id === user.unit_id;
            }
            return s.paramedic_id === user?.id || s.driver_id === user?.id;
        };
        const mineList = services.filter(isMine);

        const acceptablePending = new Set(["unidad_asignada", "tripulacion_notificada"]);

        const activosList = mineList.filter((s) => !FINISHED_STATES.has(s.status) && !acceptablePending.has(s.status));
        const pendientesList = mineList.filter((s) => acceptablePending.has(s.status));
        const historicoList = mineList.filter((s) => FINISHED_STATES.has(s.status)).slice(0, 25);

        // Fallback: si el usuario no es paramédico asignado (ej. supervisión),
        // mostrar todos los activos de la flotilla en "Activos".
        const finalActivos = mineList.length === 0
            ? services.filter((s) => !FINISHED_STATES.has(s.status) && ["en_salida", "en_ruta_origen", "arribo_origen", "valoracion_inicial", "paciente_abordo", "en_ruta_destino", "arribo_destino", "entrega_paciente"].includes(s.status)).slice(0, 20)
            : activosList;

        return { activos: finalActivos, pendientes: pendientesList, historico: historicoList, mine: mineList };
    }, [services, user]);

    // Encuentra el servicio activo principal del paramédico para mandar pings GPS.
    // Toma el primero de "activos" (estado en ruta) — máximo un servicio activo
    // por tripulación a la vez en un flujo normal.
    const activeServiceForTracking = activos.find((s) =>
        ["en_salida", "en_ruta_origen", "arribo_origen", "valoracion_inicial",
         "paciente_abordo", "en_ruta_destino", "arribo_destino"].includes(s.status)
    );

    // Determinar la unidad sobre la que se reporta GPS:
    //  1. Si el rol es "ambulancia", la unidad del login (siempre disponible).
    //  2. Si es paramédico individual, intentar deducirla del servicio activo (si lo hay).
    const trackingUnitId = useMemo(() => {
        if (user?.role === "ambulancia" && user?.unit_id) return user.unit_id;
        return activeServiceForTracking?.unit_id || null;
    }, [user, activeServiceForTracking]);

    // Tracking continuo GPS: ping al backend cada ~30s.
    //  - Si hay servicio activo → POST /services/:id/ping (acumula trayectoria + actualiza unit)
    //  - Si no hay servicio pero sí unidad → POST /units/:id/location (solo posición actual)
    // De esta forma la unidad SIEMPRE aparece en el mapa cuando hay tracking ON,
    // incluso si la tripulación ya inició turno pero aún no le asignan servicio.
    useEffect(() => {
        if (!tracking) return;
        if (!activeServiceForTracking && !trackingUnitId) return; // nada qué reportar
        let cancelled = false;
        let watchId = null;

        const sendPing = async (coords) => {
            if (cancelled) return;
            try {
                if (activeServiceForTracking) {
                    await api.post(`/services/${activeServiceForTracking.id}/ping`, {
                        lat: coords.latitude,
                        lng: coords.longitude,
                        accuracy: coords.accuracy,
                        speed: coords.speed,
                    });
                } else if (trackingUnitId) {
                    await api.post(`/units/${trackingUnitId}/location`, {
                        lat: coords.latitude,
                        lng: coords.longitude,
                    });
                }
                setLastPing(new Date().toISOString());
            } catch (e) {
                // Silencioso: no toast en cada falla (puede haber muchas si hay corte)
                console.warn("Ping fallido:", e?.message);
            }
        };

        // Estrategia híbrida: watchPosition (continuous) + interval cap (30s)
        // para evitar mandar pings demasiado frecuentes si el GPS dispara mucho.
        let lastSentAt = 0;
        const MIN_INTERVAL = 25_000; // 25s mínimo entre pings

        if (navigator.geolocation) {
            watchId = navigator.geolocation.watchPosition(
                (pos) => {
                    const now = Date.now();
                    if (now - lastSentAt >= MIN_INTERVAL) {
                        lastSentAt = now;
                        sendPing(pos.coords);
                    }
                },
                (err) => console.warn("Geolocation watch error:", err?.message),
                { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
            );
        }

        // Ping inmediato al activar
        getPosition().then((p) => {
            if (p && !cancelled) sendPing({ latitude: p.lat, longitude: p.lng, accuracy: null, speed: null });
        });

        return () => {
            cancelled = true;
            if (watchId !== null && navigator.geolocation) {
                navigator.geolocation.clearWatch(watchId);
            }
        };
    }, [tracking, activeServiceForTracking?.id, trackingUnitId]);

    const toggleTracking = () => {
        const next = !tracking;
        setTracking(next);
        try { localStorage.setItem("findmed_gps_tracking", next ? "1" : "0"); } catch (_e) { /* localStorage unavailable */ }
        if (next && !activeServiceForTracking && trackingUnitId) {
            toast.success("Tracking activo · enviando posición de la unidad cada 30s. Aparece en el mapa.");
        } else if (next && !activeServiceForTracking) {
            toast.info("Tracking activado. Empezará a enviar GPS cuando aceptes un servicio.");
        } else if (next) {
            toast.success(`Tracking activo · enviando GPS al folio ${activeServiceForTracking.folio}`);
        } else {
            toast("Tracking detenido");
        }
    };

    // ===== Inicio / Fin de turno (Attendance) =====
    const [openAttendance, setOpenAttendance] = useState(null);

    const refreshAttendance = useCallback(async () => {
        if (!user?.id) return;
        try {
            const { data } = await api.get(`/attendance?only_open=true&personnel_id=${user.id}&limit=1`);
            setOpenAttendance(Array.isArray(data) && data.length > 0 ? data[0] : null);
        } catch { /* ignore */ }
    }, [user?.id]);

    useEffect(() => { refreshAttendance(); }, [refreshAttendance]);

    const startShift = async () => {
        const pos = await getPosition();
        try {
            const { data } = await api.post("/attendance/checkin", {
                personnel_id: user.id,
                shift: "diurno",
                unit_id: trackingUnitId || null,
                lat: pos?.lat ?? null,
                lng: pos?.lng ?? null,
            });
            const linked = data?.auto_linked_services || [];
            toast.success(linked.length > 0
                ? `🪄 Turno iniciado. Vinculado a ${linked.length} servicio(s): ${linked.map((l) => l.folio).join(", ")}`
                : "🟢 Turno iniciado");
            await refreshAttendance();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "No se pudo iniciar turno");
        }
    };

    const endShift = async () => {
        if (!openAttendance) return;
        if (!window.confirm("¿Cerrar turno?\n\nSe registra tu hora de salida y se libera la unidad del sistema. Asegúrate de haber finalizado todos tus servicios primero.")) return;
        const pos = await getPosition();
        try {
            const { data } = await api.post(`/attendance/checkout/${openAttendance.id}`, {
                lat: pos?.lat ?? null,
                lng: pos?.lng ?? null,
            });
            toast.success(`🔴 Turno cerrado · ${data.duration_minutes ? Math.floor(data.duration_minutes / 60) + "h " + (data.duration_minutes % 60) + "min" : "—"}`);
            await refreshAttendance();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cerrar turno");
        }
    };

    const currentList = tab === "activos" ? activos : tab === "pendientes" ? pendientes : historico;

    const updateStatus = async (service, nextStatus, comment) => {
        setUpdatingId(service.id);
        const pos = await getPosition();
        try {
            await api.patch(`/services/${service.id}/status`, {
                status: nextStatus,
                comment: comment || null,
                lat: pos?.lat ?? null,
                lng: pos?.lng ?? null,
            });
            toast.success(`Estatus → ${nextStatus.replace(/_/g, " ")}`);
            await load();
            if (nextStatus === "servicio_finalizado") {
                // Sugerir abrir FRAP directamente
                toast("Abrir FRAP", {
                    description: "Captura el reporte prehospitalario",
                    action: { label: "Ir al FRAP", onClick: () => navigate(`/services/${service.id}/frap`) },
                });
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setUpdatingId(null);
        }
    };

    const changeDestination = async (service) => {
        const noTransport = confirm(
            "¿El paciente requirió ATENCIÓN EN EL LUGAR sin traslado?\n\n" +
            "OK = SÍ, atendido sin traslado\n" +
            "Cancelar = NO, voy a cambiar la dirección de destino"
        );
        if (noTransport) {
            try {
                await api.patch(`/services/${service.id}/logistics`, {
                    no_transport: true,
                    notes: "Atención en el lugar sin traslado (marcado desde CrewMobile)",
                });
                toast.success("Marcado: atención en el lugar, sin traslado");
                await load();
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
            }
            return;
        }
        const newDest = prompt(
            "Nueva dirección de destino:\n(Ej. Hospital Ángeles Roma · Av. Insurgentes Sur 234)",
            service.destination_address || ""
        );
        if (!newDest || !newDest.trim()) return;
        try {
            await api.patch(`/services/${service.id}/logistics`, {
                destination: newDest.trim(),
                hospital_destination: newDest.trim(),
            });
            toast.success("Destino actualizado");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-3" data-testid="crew-mobile-page">
            {/* Header */}
            <div className="fm-card p-4 bg-slate-900 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center">
                        <Smartphone className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-display font-bold text-lg leading-none">Vista Móvil · Tripulación</h3>
                        <div className="text-[11px] text-slate-400 mt-1 font-mono">
                            {user?.name} · {mine.length} servicio(s) propio(s)
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="p-2 rounded-sm hover:bg-slate-800 transition-colors"
                    data-testid="crew-refresh-btn"
                    aria-label="Refrescar"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* Selector de tripulación (solo logins de unidad) */}
            {user?.role === "ambulancia" && (
                <ShiftCard
                    unitId={user.unit_id}
                    unitLabel={unitInfo ? `${unitInfo.economic_number} · ${unitInfo.plates}` : null}
                />
            )}

            {/* Cotización rápida — útil cuando un transeúnte/cliente pregunta el costo */}
            <button
                type="button"
                onClick={() => setQuickQuoteOpen(true)}
                className="fm-card w-full p-3 flex items-center justify-between border-l-4 border-l-amber-500 hover:bg-amber-50 transition-colors"
                data-testid="quick-quote-btn"
            >
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-500 text-white rounded-sm flex items-center justify-center">
                        <Calculator className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                        <div className="font-bold text-sm">Cotización rápida</div>
                        <div className="text-[11px] text-slate-500">Cotiza un servicio en menos de 30 segundos y mándalo por WhatsApp</div>
                    </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
            </button>

            {/* GPS Tracking toggle */}
            <button
                type="button"
                onClick={toggleTracking}
                data-testid="gps-tracking-toggle"
                title="Cuando está ON, tu celular envía tu ubicación al despacho cada 30 segundos. Sin esto, cabina no sabe dónde está tu ambulancia. Si el navegador pide permiso de ubicación, pulsa 'Permitir siempre'."
                className={`fm-card p-3 w-full flex items-center justify-between transition-colors ${tracking ? "border-l-4 border-l-emerald-500" : "border-l-4 border-l-slate-300"}`}
            >
                <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${tracking ? "bg-emerald-500 text-white animate-pulse" : "bg-slate-200 text-slate-500"}`}>
                        <Radio className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                        <div className="text-xs font-bold uppercase tracking-wider">
                            {tracking ? "GPS EN VIVO ACTIVO" : "GPS en vivo apagado"}
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono">
                            {tracking
                                ? activeServiceForTracking
                                    ? `Folio ${activeServiceForTracking.folio} · ${lastPing ? `último ping ${new Date(lastPing).toLocaleTimeString()}` : "esperando primera ubicación..."}`
                                    : "Esperando servicio activo para empezar a enviar"
                                : "Activa para enviar tu ubicación al despacho cada 30s"}
                        </div>
                    </div>
                </div>
                <div className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm ${tracking ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                    {tracking ? "ON" : "OFF"}
                </div>
            </button>

            {/* Inicio / Fin de turno */}
            {user?.id && (
                <button
                    type="button"
                    onClick={openAttendance ? endShift : startShift}
                    data-testid={openAttendance ? "end-shift-btn" : "start-shift-btn"}
                    title={openAttendance ? "Cerrar turno (checkout)" : "Iniciar turno (checkin)"}
                    className={`fm-card p-3 w-full flex items-center justify-between transition-colors ${openAttendance ? "border-l-4 border-l-red-500 hover:bg-red-50" : "border-l-4 border-l-emerald-500 hover:bg-emerald-50"}`}
                >
                    <div className="flex items-center gap-2">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${openAttendance ? "bg-red-500 text-white" : "bg-emerald-500 text-white"}`}>
                            <Clock className="w-4 h-4" />
                        </div>
                        <div className="text-left">
                            <div className="text-xs font-bold uppercase tracking-wider">
                                {openAttendance ? "TURNO ACTIVO · cerrar" : "Iniciar turno"}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">
                                {openAttendance
                                    ? `Desde ${new Date(openAttendance.check_in_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}${openAttendance.unit_label ? " · " + openAttendance.unit_label : ""}`
                                    : "Registra tu hora de entrada y vincula tu unidad"}
                            </div>
                        </div>
                    </div>
                    <div className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm ${openAttendance ? "bg-red-500 text-white" : "bg-emerald-500 text-white"}`}>
                        {openAttendance ? "Cerrar" : "Iniciar"}
                    </div>
                </button>
            )}

            {/* Manual de tripulación */}
            <DownloadManualButton />

            {/* Tabs */}
            <div className="fm-card p-1 flex items-center gap-1" data-testid="crew-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const count = t.id === "activos" ? activos.length : t.id === "pendientes" ? pendientes.length : historico.length;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            data-testid={`crew-tab-${t.id}`}
                            className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {t.label}
                            {count > 0 && (
                                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${active ? "bg-red-600 text-white" : "bg-slate-200 text-slate-700"}`}>{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* List */}
            {currentList.length === 0 ? (
                <div className="fm-card p-10 text-center text-sm text-slate-500" data-testid="crew-empty">
                    <Activity className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                    {tab === "activos" && "No tienes servicios activos en este momento."}
                    {tab === "pendientes" && "Sin servicios pendientes de aceptación."}
                    {tab === "historico" && "Aún no tienes servicios cerrados."}
                </div>
            ) : (
                currentList.map((s) => {
                    const step = NEXT_STEP[s.status];
                    const isUpdating = updatingId === s.id;
                    const isFinished = FINISHED_STATES.has(s.status);
                    const isPending = s.status === "unidad_asignada" || s.status === "tripulacion_notificada";
                    const navAddress = ["en_salida", "en_ruta_origen", "arribo_origen"].includes(s.status)
                        ? { addr: s.origin_address, lat: s.origin_lat, lng: s.origin_lng, label: "origen" }
                        : { addr: s.destination_address, lat: s.destination_lat, lng: s.destination_lng, label: "destino" };
                    const navUrl = gmapsUrl(navAddress.addr, navAddress.lat, navAddress.lng);

                    return (
                        <div
                            key={s.id}
                            className={`fm-card p-4 border-l-4 ${isPending ? "border-l-amber-500" : isFinished ? "border-l-slate-300" : "border-l-red-600"}`}
                            data-testid={`crew-card-${s.folio}`}
                        >
                            <div className="flex items-start justify-between mb-2 gap-2">
                                <div className="min-w-0">
                                    <div className="font-mono text-[10px] text-slate-500">{s.folio}</div>
                                    <div className="font-display font-bold text-base truncate">{s.patient_name}</div>
                                    <div className="text-xs text-slate-500 truncate">{s.client_name || "Particular"} · {s.service_type_name}</div>
                                </div>
                                <UrgencyBadge urgency={s.urgency_level} />
                            </div>

                            <div className="space-y-1.5 my-3 text-sm">
                                <div className="flex items-start gap-2">
                                    <MapPin className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Origen</div>
                                        <div className="text-xs truncate">{s.origin_address || "—"}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Hospital className="w-3.5 h-3.5 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Destino</div>
                                        <div className="text-xs truncate">{s.destination_address || "—"}</div>
                                    </div>
                                </div>
                                {s.patient_phone && (
                                    <a href={`tel:${s.patient_phone}`} className="flex items-center gap-2 text-emerald-700 hover:underline" data-testid={`crew-call-${s.folio}`}>
                                        <Phone className="w-3.5 h-3.5" />
                                        <span className="font-mono text-xs">{s.patient_phone}</span>
                                    </a>
                                )}
                            </div>

                            <div className="border-t border-slate-200 pt-3 flex items-center justify-between mb-3">
                                <StatusBadge status={s.status} />
                                <span className="text-[10px] font-mono text-slate-500">{dateFmt(s.created_at)}</span>
                            </div>

                            {/* Acciones contextuales */}
                            {!isFinished && (
                                <div className="space-y-2">
                                    {/* Botón principal: siguiente paso */}
                                    {step && (
                                        <button
                                            type="button"
                                            onClick={() => updateStatus(s, step.next)}
                                            disabled={isUpdating}
                                            data-testid={`crew-advance-${s.folio}`}
                                            className={`${step.color} text-white w-full py-3 rounded-sm font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50`}
                                        >
                                            {isUpdating ? "Actualizando..." : (
                                                <>
                                                    {step.label} <ArrowRight className="w-4 h-4" />
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {/* Acciones secundarias */}
                                    <div className="grid grid-cols-2 gap-2">
                                        {navUrl && (
                                            <a
                                                href={navUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                data-testid={`crew-nav-${s.folio}`}
                                                className="bg-slate-100 hover:bg-slate-200 text-slate-900 py-2.5 rounded-sm font-bold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5"
                                            >
                                                <Navigation className="w-3.5 h-3.5" /> Ruta a {navAddress.label}
                                            </a>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setChargesService(s)}
                                            data-testid={`crew-charges-${s.folio}`}
                                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-900 py-2.5 rounded-sm font-bold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5 border border-emerald-300"
                                            title="Agrega km foráneo, espera, oxígeno y demás cargos al servicio"
                                        >
                                            <Receipt className="w-3.5 h-3.5" /> Cargos {s.extras?.length > 0 && <span className="bg-emerald-600 text-white px-1.5 py-0.5 rounded-sm text-[9px]">{s.extras.length}</span>}
                                        </button>
                                        <Link
                                            to={`/services/${s.id}/frap`}
                                            data-testid={`crew-frap-${s.folio}`}
                                            className="bg-slate-100 hover:bg-slate-200 text-slate-900 py-2.5 rounded-sm font-bold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5"
                                        >
                                            <FileText className="w-3.5 h-3.5" /> FRAP
                                        </Link>
                                        <Link
                                            to={`/services/${s.id}`}
                                            className="bg-slate-100 hover:bg-slate-200 text-slate-900 py-2.5 rounded-sm font-bold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5"
                                        >
                                            Ver detalle <ArrowRight className="w-3 h-3" />
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => changeDestination(s)}
                                            data-testid={`crew-change-dest-${s.folio}`}
                                            className="bg-amber-50 hover:bg-amber-100 text-amber-900 py-2.5 rounded-sm font-bold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5 border border-amber-300 col-span-2"
                                            title="Cambia el destino al que se va a trasladar, o márcalo como 'atención en el lugar sin traslado'."
                                        >
                                            Cambiar destino
                                        </button>
                                    </div>
                                </div>
                            )}

                            {isFinished && (
                                <div className="grid grid-cols-2 gap-2">
                                    <Link
                                        to={`/services/${s.id}/frap`}
                                        className="bg-slate-900 hover:bg-slate-800 text-white py-2.5 rounded-sm font-bold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5"
                                    >
                                        <FileText className="w-3.5 h-3.5" /> Ver FRAP
                                    </Link>
                                    <Link
                                        to={`/services/${s.id}`}
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-sm font-bold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Detalle
                                    </Link>
                                </div>
                            )}
                        </div>
                    );
                })
            )}
            <QuickQuoteModal open={quickQuoteOpen} onClose={() => setQuickQuoteOpen(false)} />
            {chargesService && (
                <ServiceChargesSheet
                    service={chargesService}
                    onClose={() => setChargesService(null)}
                    onUpdated={load}
                />
            )}
        </div>
    );
}
